import { useEffect, useLayoutEffect } from 'react';
import type { ReactGanttRef } from '@dhx/react-gantt';
import { getDhtmlxGanttThemeName } from '../theme/dhtmlxGanttTheme';

const GANTT_THEME_ATTR = 'data-gantt-theme';

type GanttInstance = {
  skin?: string;
  setSkin?: (skin: string) => void;
  render?: () => void;
  setSizes?: () => void;
};

function refreshGantt(gantt: GanttInstance | undefined) {
  if (!gantt?.render) return;
  gantt.render();
  gantt.setSizes?.();
}

/**
 * Syncs dhtmlx Gantt built-in theme with app light/dark mode while the Gantt page is mounted.
 *
 * Keeps ReactGantt skin aligned with app color scheme: terrace (light) / dark.
 * Also keeps `data-gantt-theme` on <html> for CSS variable packs.
 * Grid/chrome tokens stay on `.fbt-gantt-root` via themeVars.
 */
export function useDhtmlxGanttColorScheme(
  colorScheme: 'light' | 'dark',
  ganttRef: React.RefObject<ReactGanttRef | null>,
) {
  const ganttTheme = getDhtmlxGanttThemeName(colorScheme);

  useLayoutEffect(() => {
    document.documentElement.setAttribute(GANTT_THEME_ATTR, ganttTheme);
    return () => {
      document.documentElement.removeAttribute(GANTT_THEME_ATTR);
    };
  }, [ganttTheme]);

  useEffect(() => {
    let cancelled = false;
    let raf2 = 0;

    const runRefresh = () => {
      if (cancelled) return;
      const gantt = ganttRef.current?.instance as GanttInstance | undefined;
      if (gantt?.setSkin && gantt.skin !== ganttTheme) {
        gantt.setSkin(ganttTheme);
        return;
      }
      refreshGantt(gantt);
    };

    const raf1 = requestAnimationFrame(() => {
      runRefresh();
      raf2 = requestAnimationFrame(runRefresh);
    });

    const lateTimer = window.setTimeout(runRefresh, 50);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      window.clearTimeout(lateTimer);
    };
  }, [ganttTheme, ganttRef]);
}
